<template>
  <b-card class="shadow-sm top-products-card">
    <b-card-title
      class="mb-4 pt-6 px-6 d-flex justify-content-between align-items-start"
    >
      <div class="d-flex align-items-center">
        <b-icon icon="trophy-fill" variant="primary" class="mr-2" />
        <span>Produtos Mais Vendidos</span>
      </div>

      <div class="d-flex mt-1">
        <!-- Filtro de tipo de ranking -->
        <b-dropdown
          :text="parametro.label"
          size="sm"
          variant="light"
          class="filtro-dropdown"
        >
          <b-dropdown-item
            v-for="param in parametros"
            :key="param.id"
            @click="updateParameter(param)"
          >
            {{ param.label }}
          </b-dropdown-item>
        </b-dropdown>

        <!-- Filtro de período -->
        <FiltroPeriodo
          class="ml-3"
          :value="periodo"
          default-label="Hoje"
          :auto-apply-on-mount="!ignoreInitialApply"
          @apply="onApplyPeriodo"
        />
      </div>
    </b-card-title>

    <b-card-body class="pt-2 pb-2 px-6">
      <div class="top-products-scroll">
        <!-- LOADING -->
        <template v-if="loading">
          <ul class="list-unstyled m-0 p-0">
            <li
              v-for="i in 3"
              :key="i"
              class="top-product-item d-flex align-items-center"
            >
              <div
                class="top-product-position d-flex justify-content-center align-items-center rounded-circle flex-shrink-0"
                style="background-color: #f3f6f9"
              >
                <b-skeleton width="18px" height="18px" />
              </div>

              <div class="flex-grow-1 top-product-content">
                <div class="d-flex justify-content-between align-items-center">
                  <b-skeleton width="55%" height="16px" />
                  <b-skeleton width="18%" height="14px" />
                </div>

                <b-skeleton height="5px" class="mt-2" />
              </div>
            </li>
          </ul>
        </template>

        <!-- LISTA -->
        <template v-else>
          <ul
            v-if="sortedProducts.length"
            class="list-unstyled m-0 p-0"
          >
            <li
              v-for="(item, index) in sortedProducts"
              :key="`${item.produto}-${index}`"
              class="top-product-item d-flex align-items-center"
            >
              <!-- POSIÇÃO -->
              <div
                class="top-product-position d-flex justify-content-center align-items-center rounded-circle flex-shrink-0"
                :style="{ 'background-color': medalHexColor(index) }"
              >
                <span v-if="index === 0">🥇</span>
                <span v-else-if="index === 1">🥈</span>
                <span v-else-if="index === 2">🥉</span>

                <span
                  v-else
                  class="top-product-position-number"
                >
                  {{ index + 1 }}
                </span>
              </div>

              <!-- PRODUTO -->
              <div class="flex-grow-1 top-product-content">
                <div class="top-product-header">
                  <p
                    class="top-product-name"
                    :title="item.produto"
                  >
                    {{ item.produto }}
                  </p>

                  <small class="top-product-value">
                    <span v-if="parametro.id === 'qtd-vendas'">
                      {{ item.qtd }} unidades
                    </span>

                    <span v-else>
                      {{ formatCurrency(item.valor) }}
                    </span>
                  </small>
                </div>

                <!-- BARRA -->
                <b-progress
                  :value="barValue(item)"
                  :max="barMax"
                  height="5px"
                  :class="{
                    'top1-progress-bar': index === 0,
                    'top2-progress-bar': index === 1,
                    'top3-progress-bar': index === 2,
                    'neutral-progress-bar': index > 2,
                  }"
                  class="rounded"
                />
              </div>
            </li>
          </ul>

          <!-- SEM DADOS -->
          <div v-else class="text-center py-5">
            <i class="far fa-box-open fa-2x text-muted mb-2"></i>

            <div class="text-muted">
              Nenhum produto vendido no período
            </div>
          </div>
        </template>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import FiltroPeriodo from "./FiltroPeriodo.vue";

export default {
  name: "TopProdutosVendidos",

  components: {
    FiltroPeriodo,
  },

  props: {
    /**
     * Aceita:
     * { data: [{ produto, qtd, valor }] }
     *
     * ou:
     * [{ produto, qtd, valor }]
     */
    dados: {
      type: [Object, Array],
      default: () => ({ data: [] }),
    },

    periodo: {
      type: Object,
      default: null,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    ignoreInitialApply: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      parametros: [
        {
          id: "qtd-vendas",
          label: "Qtd. vendas",
        },
        {
          id: "valor-monetario",
          label: "Valor monetário",
        },
      ],

      parametro: {
        id: "qtd-vendas",
        label: "Qtd. vendas",
      },
    };
  },

  computed: {
    products() {
      if (Array.isArray(this.dados)) {
        return this.dados;
      }

      if (this.dados && Array.isArray(this.dados.data)) {
        return this.dados.data;
      }

      return [];
    },

    sortedProducts() {
      const arr = [...this.products];

      const key =
        this.parametro.id === "qtd-vendas"
          ? "qtd"
          : "valor";

      return arr.sort((a, b) => {
        const na = Number(a?.[key]) || 0;
        const nb = Number(b?.[key]) || 0;

        if (nb !== na) {
          return nb - na;
        }

        const other =
          key === "qtd"
            ? "valor"
            : "qtd";

        const oa = Number(a?.[other]) || 0;
        const ob = Number(b?.[other]) || 0;

        if (ob !== oa) {
          return ob - oa;
        }

        return String(a?.produto || "").localeCompare(
          String(b?.produto || "")
        );
      });
    },

    barMax() {
      if (!this.sortedProducts.length) {
        return 1;
      }

      const first = this.sortedProducts[0] || {};

      const raw =
        this.parametro.id === "qtd-vendas"
          ? first.qtd
          : first.valor;

      const value = Number(raw);

      return Number.isFinite(value) && value > 0
        ? value
        : 1;
    },
  },

  methods: {
    updateParameter(param) {
      this.parametro = param;
    },

    onApplyPeriodo({ inicio, fim }) {
      this.$emit("change-periodo", {
        inicio,
        fim,
      });
    },

    barValue(item) {
      const raw =
        this.parametro.id === "qtd-vendas"
          ? item.qtd
          : item.valor;

      const value = Number(raw);

      return Number.isFinite(value)
        ? value
        : 0;
    },

    formatCurrency(value) {
      try {
        return new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
        }).format(value ?? 0);
      } catch {
        const number = Number(value ?? 0)
          .toFixed(2)
          .replace(".", ",");

        return `R$ ${number}`;
      }
    },

    medalHexColor(index) {
      switch (index) {
        case 0:
          return "#FFB700";

        case 1:
          return "#D3D3D3";

        case 2:
          return "#CD7F32";

        default:
          return "#F3F6F9";
      }
    },
  },
};
</script>

<style>
.top-products-card {
  overflow: visible;
  position: relative;
}

.top-products-card:focus-within {
  z-index: 11;
}

/*
 * Mostra aproximadamente 3 produtos completos.
 * A partir do quarto item, utiliza scroll interno.
 */
.top-products-scroll {
  height: 210px;

  overflow-y: auto;
  overflow-x: hidden;

  padding-right: 6px;

  overscroll-behavior: contain;

  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}

/*
 * Scroll aparece de forma discreta quando há interação.
 */
.top-products-scroll:hover {
  scrollbar-color: #d8dbe2 transparent;
}

.top-products-scroll::-webkit-scrollbar {
  width: 5px;
}

.top-products-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.top-products-scroll::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 10px;
}

.top-products-scroll:hover::-webkit-scrollbar-thumb {
  background-color: #d8dbe2;
}

/*
 * Cada produto ganhou mais altura para facilitar leitura.
 */
.top-product-item {
  min-height: 66px;

  padding: 6px 0;

  /*
   * Separador muito discreto entre os produtos.
   */
  border-bottom: 1px solid #f1f2f5;
}

.top-product-item:last-child {
  border-bottom: none;
}

/*
 * Posição / medalha
 */
.top-product-position {
  width: 38px;
  height: 38px;
  min-width: 38px;

  margin-right: 14px;

  font-size: 1.35rem;
}

.top-product-position-number {
  color: #3f4254;
  font-size: 14px;
  font-weight: 600;
}

/*
 * Conteúdo principal
 */
.top-product-content {
  min-width: 0;
}

/*
 * Nome + valor ficam na mesma linha.
 */
.top-product-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  min-width: 0;

  margin-bottom: 7px;
}

/*
 * Nome do produto
 */
.top-product-name {
  flex: 1;
  min-width: 0;

  margin: 0 14px 0 0;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  color: #3f4254;

  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
}

/*
 * Quantidade / valor monetário
 */
.top-product-value {
  flex-shrink: 0;

  color: #7e8299;

  font-size: 12px;
  font-weight: 500;

  white-space: nowrap;
}

/*
 * Barras
 */
.top1-progress-bar .progress-bar {
  background-color: #ffb700;
}

.top2-progress-bar .progress-bar {
  background-color: #d3d3d3;
}

.top3-progress-bar .progress-bar {
  background-color: #cd7f32;
}

.neutral-progress-bar .progress-bar {
  background-color: #e9ebef;
}

/*
 * Filtro
 */
.filtro-dropdown .dropdown-toggle {
  color: var(--primary-color);
}

/*
 * Mobile
 */
@media (max-width: 576px) {
  .top-products-scroll {
    height: 205px;
  }

  .top-product-item {
    min-height: 64px;
  }

  .top-product-position {
    width: 34px;
    height: 34px;
    min-width: 34px;

    margin-right: 10px;

    font-size: 1.2rem;
  }

  .top-product-name {
    font-size: 12px;
    margin-right: 8px;
  }

  .top-product-value {
    font-size: 11px;
  }
}
</style>